import { type ReactNode } from "react"
import {
  Tabs as AriaTabs,
  type TabsProps as AriaTabsProps,
  TabList,
  Tab,
  TabPanel,
  type Key,
  Collection,
} from "react-aria-components"
import { Icon } from "../../icon/Icon"

export type TabKey = Key

export type TabsProps = AriaTabsProps & {
  className?: string
  label?: string
  tabs: {
    id: string
    title: ReactNode
    icon?: React.ElementType | React.ForwardRefExoticComponent<unknown>
    content: ReactNode | (() => ReactNode)
    isDisabled?: boolean
    href?: string
  }[]
}

/**
 * A way to switch between multiple views of content. Tab options are displayed
 * horizontally in a row. For a vertical layout, use VerticalTabs.
 *
 * ```tsx
 * import { Tabs } from "@tokenterminal/core/ui/navigation/tabs/Tabs"
 *
 * <Tabs
 *   tabs={[
 *    { id: "tab1", title: "Tab 1", content: "Content 1" },
 *    { id: "tab2", title: "Tab 2", content: "Content 2" },
 *   ]}
 * />
 * ```
 */

export const Tabs = ({ label, tabs, ...props }: TabsProps) => {
  return (
    <AriaTabs className="tt-tabs-wrapper" {...props}>
      <TabList aria-label={label} className="tt-tab-list">
        {tabs.map(({ href, icon, id, isDisabled, title }) => (
          <Tab
            key={id}
            id={id}
            isDisabled={isDisabled}
            href={href}
            className="tt-tab"
          >
            {icon && <Icon as={icon} width="14px" />}
            {title}
          </Tab>
        ))}
      </TabList>
      <Collection items={tabs}>
        {(tab) => (
          <TabPanel key={tab.id} id={tab.id} className="tt-tab-panel">
            {typeof tab.content === "function" ? tab.content() : tab.content}
          </TabPanel>
        )}
      </Collection>
    </AriaTabs>
  )
}
